import { ChevronLeft, ChevronRight } from 'lucide-react'

import { cn } from '@/lib/utils'

interface PaginationProps {
  page: number
  totalCount: number
  pageSize: number
  onPage: (p: number) => void
}

/**
 * Page numbers to show: always the first and last page, the current page and its
 * neighbours, with gaps ('…') between. Always 7 slots once there are more than 7
 * pages, so the buttons don't shift around while stepping:
 *   1 2 3 4 5 … 57   ·   1 … 27 28 29 … 57   ·   1 … 53 54 55 56 57
 */
export function pageItems(page: number, totalPages: number): (number | 'gap')[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
  if (page <= 4) return [1, 2, 3, 4, 5, 'gap', totalPages]
  if (page >= totalPages - 3) return [1, 'gap', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  return [1, 'gap', page - 1, page, page + 1, 'gap', totalPages]
}

const stepClass =
  'flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-sm tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40'

export function Pagination({ page, totalCount, pageSize, onPage }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  if (totalPages <= 1) return null

  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, totalCount)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
      <span className="text-xs tabular-nums text-gray-500">
        {start}–{end} of {totalCount}
      </span>
      <nav className="flex items-center gap-1" aria-label="Pages">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          aria-label="Previous page"
          className={cn(stepClass, 'text-gray-500 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-30')}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {pageItems(page, totalPages).map((item, i) =>
          item === 'gap' ? (
            <span key={`gap-${i}`} className="flex h-8 min-w-8 items-center justify-center text-sm text-gray-400" aria-hidden>
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => item !== page && onPage(item)}
              aria-label={`Page ${item}`}
              aria-current={item === page ? 'page' : undefined}
              className={cn(
                stepClass,
                item === page ? 'bg-primary font-semibold text-primary-foreground' : 'text-gray-700 hover:bg-gray-100',
              )}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
          aria-label="Next page"
          className={cn(stepClass, 'text-gray-500 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-30')}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </nav>
    </div>
  )
}
